import { DashboardCard } from "@dashboard/components/Card";
import { Pill } from "@dashboard/components/Pill";
import { CheckoutChargeStatusEnum } from "@dashboard/graphql";
import { PillStatusType } from "@dashboard/misc";
import SummaryLine from "@dashboard/orders/components/OrderSummaryCard/SummaryLine";
import { SummaryList } from "@dashboard/orders/components/OrderSummaryCard/SummaryList";
import React from "react";
import { useIntl } from "react-intl";

import { paymentRecoveryMessages } from "../../messages";
import { getHeldMoney, PaymentRecoveryCheckoutDetails } from "../../utils";

interface PaymentRecoveryPaymentSummaryProps {
  checkout: PaymentRecoveryCheckoutDetails;
}

const chargeStatusPillColor: Record<CheckoutChargeStatusEnum, PillStatusType> = {
  [CheckoutChargeStatusEnum.FULL]: "success",
  [CheckoutChargeStatusEnum.PARTIAL]: "warning",
  [CheckoutChargeStatusEnum.OVERCHARGED]: "warning",
  [CheckoutChargeStatusEnum.NONE]: "generic",
};

/**
 * A checkout-flavored fork of OrderPaymentSummaryCard
 * (src/orders/components/OrderPaymentSummaryCard) -- same SummaryLine
 * breakdown pattern and status-pill placement in the header toolbar, but
 * without any of that component's Order-only machinery (grant/send refund
 * links, mark-as-paid, gift cards): this checkout has none of that, and the
 * page's own Refund/Create order actions already cover the equivalent
 * decisions here.
 */
export const PaymentRecoveryPaymentSummary = ({ checkout }: PaymentRecoveryPaymentSummaryProps) => {
  const intl = useIntl();
  const held = getHeldMoney(checkout.transactions);

  return (
    <DashboardCard data-test-id="payment-recovery-payment-summary">
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage(paymentRecoveryMessages.paymentSummarySection)}
        </DashboardCard.Title>
        <DashboardCard.Toolbar>
          <Pill
            color={chargeStatusPillColor[checkout.chargeStatus]}
            label={checkout.chargeStatus.toLowerCase()}
          />
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <SummaryList>
          <SummaryLine
            text={intl.formatMessage(paymentRecoveryMessages.authorized)}
            money={held.authorized}
          />
          <SummaryLine
            text={intl.formatMessage(paymentRecoveryMessages.captured)}
            money={held.charged}
          />
        </SummaryList>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

PaymentRecoveryPaymentSummary.displayName = "PaymentRecoveryPaymentSummary";
export default PaymentRecoveryPaymentSummary;
